import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactTable from 'react-table';
import ScrollArea from 'react-scrollbar';
import Select from 'react-select';
import { ScrollModule } from '../ScrollModule';
import { EnterprisesService } from '../../services';
import { TIMEFRAME_OPTIONS } from './enterprise_card';

// ---------------------------------------------------------------------------
// Data helpers
// ---------------------------------------------------------------------------

function buildSummary(locations) {
    let criticalEvents = 0;
    let locationsOffline = 0;
    let locationsTotal = 0;
    let gatewaysOffline = 0, gatewaysTotal = 0;
    let sensorsOffline = 0, sensorsTotal = 0;
    let alertsDisabled = 0;

    locations.forEach(loc => {
        const isUnassigned = loc.deviceGroupingId === null;
        (loc.deviceCounts || []).forEach(dc => {
            if (dc.functionalType === 'gateway') {
                gatewaysOffline += dc.offline;
                gatewaysTotal   += dc.total;
            } else if (dc.functionalType === 'sensor') {
                sensorsOffline += dc.offline;
                sensorsTotal   += dc.total;
            }
        });
        if (!isUnassigned) {
            locationsTotal++;
            criticalEvents += loc.criticalAlertCount || 0;
            if (loc.isOffline) locationsOffline++;
            if (!loc.actionsEnabled) alertsDisabled++;
        }
    });

    return {
        criticalEvents,
        locationsOffline, locationsTotal,
        gatewaysOffline,  gatewaysTotal,
        sensorsOffline,   sensorsTotal,
        alertsDisabled,
    };
}

const FIXED_DEVICE_COLS = [
    { key: 'lora_gateway',      protocol: 'lora',      functionalType: 'gateway', label: 'LoRa Gateways Offline'      },
    { key: 'lora_sensor',       protocol: 'lora',      functionalType: 'sensor',  label: 'LoRa Sensors Offline'       },
    { key: 'wifi_sensor',       protocol: 'wifi',      functionalType: 'sensor',  label: 'WiFi Sensors Offline'       },
    { key: 'ethernet_gateway',  protocol: 'ethernet',  functionalType: 'gateway', label: 'Zigbee Gateways Offline'    },
    { key: 'zigbee_sensor',     protocol: 'zigbee',    functionalType: 'sensor',  label: 'Zigbee Sensors Offline'     },
];

function getCellOffline(loc, col) {
    const dc = (loc.deviceCounts || []).find(
        d => d.protocol === col.protocol && d.functionalType === col.functionalType
    );
    return dc ? dc.offline : null; // null renders as '--'
}

function filterByTab(locations, tab) {
    switch (tab) {
        case 'attention':
            return locations.filter(loc =>
                loc.criticalAlertCount > 0 ||
                (loc.deviceCounts || []).some(dc => dc.offline > 0)
            );
        case 'offline':
            return locations.filter(loc => loc.isOffline);
        case 'other':
            return locations.filter(loc =>
                loc.criticalAlertCount === 0 &&
                !(loc.deviceCounts || []).some(dc => dc.offline > 0)
            );
        default:
            return locations;
    }
}

function nullSafeSort(a, b) {
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return a - b;
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

const BuildingIcon = () => (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clipPath="url(#clip0_148_302)">
            <path d="M23.0234 4.60254V25.9307" stroke="#1B82B7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M4.97656 4.60254H23.0234" stroke="#1B82B7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M4.97656 25.9307V4.60254" stroke="#1B82B7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M23.0234 25.9307H4.97656" stroke="#1B82B7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M23.0234 4.60254H4.97656L6.61719 1.32129H21.3828L23.0234 4.60254Z" stroke="#1B82B7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M1.69531 25.9307H26.3047" stroke="#1B82B7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M16.4609 23.4697C16.4609 22.817 16.2017 22.1911 15.7401 21.7296C15.2786 21.2681 14.6527 21.0088 14 21.0088C13.3473 21.0088 12.7214 21.2681 12.2599 21.7296C11.7983 22.1911 11.5391 22.817 11.5391 23.4697V25.9307H16.4609V23.4697Z" stroke="#1B82B7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M9.07812 8.29395C8.96934 8.29395 8.86502 8.25073 8.7881 8.17381C8.71118 8.09689 8.66797 7.99257 8.66797 7.88379C8.66797 7.77501 8.71118 7.67068 8.7881 7.59376C8.86502 7.51685 8.96934 7.47363 9.07812 7.47363" stroke="#1B82B7"/>
            <path d="M9.07812 8.29395C9.18691 8.29395 9.29123 8.25073 9.36815 8.17381C9.44507 8.09689 9.48828 7.99257 9.48828 7.88379C9.48828 7.77501 9.44507 7.67068 9.36815 7.59376C9.29123 7.51685 9.18691 7.47363 9.07812 7.47363" stroke="#1B82B7"/>
            <path d="M14 8.29395C13.8912 8.29395 13.7869 8.25073 13.71 8.17381C13.6331 8.09689 13.5898 7.99257 13.5898 7.88379C13.5898 7.77501 13.6331 7.67068 13.71 7.59376C13.7869 7.51685 13.8912 7.47363 14 7.47363" stroke="#1B82B7"/>
            <path d="M14 8.29395C14.1088 8.29395 14.2131 8.25073 14.29 8.17381C14.3669 8.09689 14.4102 7.99257 14.4102 7.88379C14.4102 7.77501 14.3669 7.67068 14.29 7.59376C14.2131 7.51685 14.1088 7.47363 14 7.47363" stroke="#1B82B7"/>
            <path d="M18.9219 8.29395C18.8131 8.29395 18.7088 8.25073 18.6319 8.17381C18.5549 8.09689 18.5117 7.99257 18.5117 7.88379C18.5117 7.77501 18.5549 7.67068 18.6319 7.59376C18.7088 7.51685 18.8131 7.47363 18.9219 7.47363" stroke="#1B82B7"/>
            <path d="M18.9219 8.29395C19.0307 8.29395 19.135 8.25073 19.2119 8.17381C19.2888 8.09689 19.332 7.99257 19.332 7.88379C19.332 7.77501 19.2888 7.67068 19.2119 7.59376C19.135 7.51685 19.0307 7.47363 18.9219 7.47363" stroke="#1B82B7"/>
            <path d="M9.07812 13.2158C8.96934 13.2158 8.86502 13.1726 8.7881 13.0957C8.71118 13.0188 8.66797 12.9144 8.66797 12.8057C8.66797 12.6969 8.71118 12.5926 8.7881 12.5156C8.86502 12.4387 8.96934 12.3955 9.07812 12.3955" stroke="#1B82B7"/>
            <path d="M9.07812 13.2158C9.18691 13.2158 9.29123 13.1726 9.36815 13.0957C9.44507 13.0188 9.48828 12.9144 9.48828 12.8057C9.48828 12.6969 9.44507 12.5926 9.36815 12.5156C9.29123 12.4387 9.18691 12.3955 9.07812 12.3955" stroke="#1B82B7"/>
            <path d="M14 13.2158C13.8912 13.2158 13.7869 13.1726 13.71 13.0957C13.6331 13.0188 13.5898 12.9144 13.5898 12.8057C13.5898 12.6969 13.6331 12.5926 13.71 12.5156C13.7869 12.4387 13.8912 12.3955 14 12.3955" stroke="#1B82B7"/>
            <path d="M14 13.2158C14.1088 13.2158 14.2131 13.1726 14.29 13.0957C14.3669 13.0188 14.4102 12.9144 14.4102 12.8057C14.4102 12.6969 14.3669 12.5926 14.29 12.5156C14.2131 12.4387 14.1088 12.3955 14 12.3955" stroke="#1B82B7"/>
            <path d="M18.9219 13.2158C18.8131 13.2158 18.7088 13.1726 18.6319 13.0957C18.5549 13.0188 18.5117 12.9144 18.5117 12.8057C18.5117 12.6969 18.5549 12.5926 18.6319 12.5156C18.7088 12.4387 18.8131 12.3955 18.9219 12.3955" stroke="#1B82B7"/>
            <path d="M18.9219 13.2158C19.0307 13.2158 19.135 13.1726 19.2119 13.0957C19.2888 13.0188 19.332 12.9144 19.332 12.8057C19.332 12.6969 19.2888 12.5926 19.2119 12.5156C19.135 12.4387 19.0307 12.3955 18.9219 12.3955" stroke="#1B82B7"/>
            <path d="M9.07812 18.1377C8.96934 18.1377 8.86502 18.0945 8.7881 18.0176C8.71118 17.9406 8.66797 17.8363 8.66797 17.7275C8.66797 17.6188 8.71118 17.5144 8.7881 17.4375C8.86502 17.3606 8.96934 17.3174 9.07812 17.3174" stroke="#1B82B7"/>
            <path d="M9.07812 18.1377C9.18691 18.1377 9.29123 18.0945 9.36815 18.0176C9.44507 17.9406 9.48828 17.8363 9.48828 17.7275C9.48828 17.6188 9.44507 17.5144 9.36815 17.4375C9.29123 17.3606 9.18691 17.3174 9.07812 17.3174" stroke="#1B82B7"/>
            <path d="M14 18.1377C13.8912 18.1377 13.7869 18.0945 13.71 18.0176C13.6331 17.9406 13.5898 17.8363 13.5898 17.7275C13.5898 17.6188 13.6331 17.5144 13.71 17.4375C13.7869 17.3606 13.8912 17.3174 14 17.3174" stroke="#1B82B7"/>
            <path d="M14 18.1377C14.1088 18.1377 14.2131 18.0945 14.29 18.0176C14.3669 17.9406 14.4102 17.8363 14.4102 17.7275C14.4102 17.6188 14.3669 17.5144 14.29 17.4375C14.2131 17.3606 14.1088 17.3174 14 17.3174" stroke="#1B82B7"/>
            <path d="M18.9219 18.1377C18.8131 18.1377 18.7088 18.0945 18.6319 18.0176C18.5549 17.9406 18.5117 17.8363 18.5117 17.7275C18.5117 17.6188 18.5549 17.5144 18.6319 17.4375C18.7088 17.3606 18.8131 17.3174 18.9219 17.3174" stroke="#1B82B7"/>
            <path d="M18.9219 18.1377C19.0307 18.1377 19.135 18.0945 19.2119 18.0176C19.2888 17.9406 19.332 17.8363 19.332 17.7275C19.332 17.6188 19.2888 17.5144 19.2119 17.4375C19.135 17.3606 19.0307 17.3174 18.9219 17.3174" stroke="#1B82B7"/>
        </g>
        <defs>
            <clipPath id="clip0_148_302">
                <rect width="28" height="28" fill="white" transform="translate(0 -0.374023)"/>
            </clipPath>
        </defs>
    </svg>
);

const AttentionTabIcon = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="9" cy="9" r="9" fill="#D71F34"/>
        <path d="M9 5V10" stroke="white" strokeWidth="1.8" strokeLinecap="round"/>
        <circle cx="9" cy="13" r="1" fill="white"/>
    </svg>
);

const OfflineTabIcon = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M8.99995 17.7858C13.8522 17.7858 17.7857 13.8523 17.7857 9.00007C17.7857 4.14785 13.8522 0.214355 8.99995 0.214355C4.14773 0.214355 0.214233 4.14785 0.214233 9.00007C0.214233 13.8523 4.14773 17.7858 8.99995 17.7858Z" fill="#9F9A9A"/>
        <g filter="url(#filter0_d_544_26)">
            <path d="M10.4598 8.83957C10.4598 9.52777 9.90247 10.0851 9.21427 10.0851C8.52606 10.0851 7.96873 9.52777 7.96873 8.83957C7.96873 8.15136 8.52606 7.59403 9.21427 7.59403C9.90247 7.59403 10.4598 8.15136 10.4598 8.83957ZM7.38105 6.1141L7.38104 6.11428C7.61036 6.35533 7.61036 6.74561 7.38272 6.98577C6.9115 7.48111 6.65196 8.13981 6.65196 8.83999C6.65196 9.54016 6.91066 10.1989 7.38188 10.6933C7.61036 10.9344 7.6112 11.3238 7.38188 11.5648C7.2668 11.6858 7.11729 11.7458 6.96694 11.7458C6.81659 11.7458 6.66622 11.6858 6.55199 11.5657C5.859 10.838 5.47766 9.86952 5.47766 8.8399C5.47766 7.81027 5.859 6.84255 6.55199 6.1141C6.78046 5.87305 7.15174 5.87305 7.38105 6.1141ZM3.73384 8.83935V8.83957C3.73384 10.3314 4.29216 11.7343 5.30729 12.7901C5.53445 13.0255 5.53445 13.408 5.30729 13.6443C5.19329 13.7629 5.04519 13.8217 4.89624 13.8217C4.74731 13.8217 4.59836 13.762 4.4852 13.6443C3.25122 12.3609 2.57141 10.6543 2.57141 8.83946C2.57141 7.02457 3.25122 5.31802 4.48603 4.03461C4.71235 3.79836 5.08014 3.79836 5.30729 4.03461C5.53445 4.27 5.53445 4.65252 5.30729 4.88878C4.29216 5.94465 3.73384 7.34738 3.73384 8.83935ZM13.9428 4.03462V4.0344C15.1771 5.31783 15.8571 7.02369 15.8571 8.83935C15.8571 10.6543 15.1771 12.3609 13.9436 13.6443C13.8296 13.7629 13.6814 13.8217 13.5325 13.8217C13.3835 13.8217 13.2345 13.762 13.1213 13.6443C12.8949 13.408 12.8949 13.0264 13.1213 12.7901C14.1367 11.7342 14.6952 10.3315 14.6952 8.83946C14.6952 7.34666 14.1359 5.94375 13.1213 4.8888C12.8941 4.6534 12.8941 4.27088 13.1213 4.03462C13.3477 3.79836 13.7156 3.79836 13.9428 4.03462ZM11.8755 6.11413V6.114C12.5681 6.84175 12.9492 7.81052 12.9509 8.83848C12.9509 9.86918 12.5697 10.8371 11.8771 11.5648C11.7621 11.6858 11.6127 11.7458 11.4624 11.7458C11.3122 11.7458 11.1619 11.6858 11.0477 11.5648C10.8185 11.3237 10.8185 10.9342 11.0477 10.6931C11.5178 10.1986 11.7772 9.53976 11.7772 8.83945C11.7772 8.13915 11.5178 7.48121 11.0469 6.98578C10.8177 6.74557 10.8177 6.35522 11.0469 6.11413C11.2761 5.87304 11.6463 5.87304 11.8755 6.11413Z" fill="#333333"/>
        </g>
        <path d="M10.4598 8.83957C10.4598 9.52777 9.90247 10.0851 9.21427 10.0851C8.52606 10.0851 7.96873 9.52777 7.96873 8.83957C7.96873 8.15136 8.52606 7.59403 9.21427 7.59403C9.90247 7.59403 10.4598 8.15136 10.4598 8.83957ZM7.38105 6.1141L7.38104 6.11428C7.61036 6.35533 7.61036 6.74561 7.38272 6.98577C6.9115 7.48111 6.65196 8.13981 6.65196 8.83999C6.65196 9.54016 6.91066 10.1989 7.38188 10.6933C7.61036 10.9344 7.6112 11.3238 7.38188 11.5648C7.2668 11.6858 7.11729 11.7458 6.96694 11.7458C6.81659 11.7458 6.66622 11.6858 6.55199 11.5657C5.859 10.838 5.47766 9.86952 5.47766 8.8399C5.47766 7.81027 5.859 6.84255 6.55199 6.1141C6.78046 5.87305 7.15174 5.87305 7.38105 6.1141ZM3.73384 8.83935V8.83957C3.73384 10.3314 4.29216 11.7343 5.30729 12.7901C5.53445 13.0255 5.53445 13.408 5.30729 13.6443C5.19329 13.7629 5.04519 13.8217 4.89624 13.8217C4.74731 13.8217 4.59836 13.762 4.4852 13.6443C3.25122 12.3609 2.57141 10.6543 2.57141 8.83946C2.57141 7.02457 3.25122 5.31802 4.48603 4.03461C4.71235 3.79836 5.08014 3.79836 5.30729 4.03461C5.53445 4.27 5.53445 4.65252 5.30729 4.88878C4.29216 5.94465 3.73384 7.34738 3.73384 8.83935ZM13.9428 4.03462V4.0344C15.1771 5.31783 15.8571 7.02369 15.8571 8.83935C15.8571 10.6543 15.1771 12.3609 13.9436 13.6443C13.8296 13.7629 13.6814 13.8217 13.5325 13.8217C13.3835 13.8217 13.2345 13.762 13.1213 13.6443C12.8949 13.408 12.8949 13.0264 13.1213 12.7901C14.1367 11.7342 14.6952 10.3315 14.6952 8.83946C14.6952 7.34666 14.1359 5.94375 13.1213 4.8888C12.8941 4.6534 12.8941 4.27088 13.1213 4.03462C13.3477 3.79836 13.7156 3.79836 13.9428 4.03462ZM11.8755 6.11413V6.114C12.5681 6.84175 12.9492 7.81052 12.9509 8.83848C12.9509 9.86918 12.5697 10.8371 11.8771 11.5648C11.7621 11.6858 11.6127 11.7458 11.4624 11.7458C11.3122 11.7458 11.1619 11.6858 11.0477 11.5648C10.8185 11.3237 10.8185 10.9342 11.0477 10.6931C11.5178 10.1986 11.7772 9.53976 11.7772 8.83945C11.7772 8.13915 11.5178 7.48121 11.0469 6.98578C10.8177 6.74557 10.8177 6.35522 11.0469 6.11413C11.2761 5.87304 11.6463 5.87304 11.8755 6.11413Z" fill="white"/>
        <defs>
            <filter id="filter0_d_544_26" x="0.571411" y="3.85742" width="17.2858" height="13.9644" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                <feOffset dy="2"/>
                <feGaussianBlur stdDeviation="1"/>
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0"/>
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_544_26"/>
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_544_26" result="shape"/>
            </filter>
        </defs>
    </svg>
);

const OtherTabIcon = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="9" cy="9" r="9" fill="#19B08D"/>
        <path d="M5.5 9L7.5 11.5L12.5 6.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

// ---------------------------------------------------------------------------
// Summary bar
// ---------------------------------------------------------------------------

const SummaryBar = ({ summary }) => {
    const { criticalEvents, locationsOffline, locationsTotal, gatewaysOffline, gatewaysTotal, sensorsOffline, sensorsTotal, alertsDisabled } = summary;

    return (
        <div className="enterprise-detail__summary-bar">
            <div className="enterprise-detail__summary-section">
                <div className="enterprise-detail__summary-section-label">Events</div>
                <div className="enterprise-detail__summary-metrics">
                    <div className="enterprise-detail__summary-metric">
                        <span className={`enterprise-detail__summary-value ${criticalEvents > 0 ? 'is-alert' : ''}`}>
                            {criticalEvents}
                        </span>
                        <span className="enterprise-detail__summary-sublabel">Critical</span>
                    </div>
                </div>
            </div>

            <div className="enterprise-detail__summary-divider" />

            <div className="enterprise-detail__summary-section enterprise-detail__summary-section--wide">
                <div className="enterprise-detail__summary-section-label">Offline</div>
                <div className="enterprise-detail__summary-metrics">
                    <div className="enterprise-detail__summary-metric">
                        <span className={`enterprise-detail__summary-value ${locationsOffline > 0 ? 'is-alert' : ''}`}>
                            {locationsOffline}/{locationsTotal}
                        </span>
                        <span className={`enterprise-detail__summary-sublabel ${locationsOffline > 0 ? 'is-alert' : ''}`}>Locations</span>
                    </div>
                    <div className="enterprise-detail__summary-metric">
                        <span className={`enterprise-detail__summary-value ${gatewaysOffline > 0 ? 'is-alert' : ''}`}>
                            {gatewaysOffline}/{gatewaysTotal}
                        </span>
                        <span className={`enterprise-detail__summary-sublabel ${gatewaysOffline > 0 ? 'is-alert' : ''}`}>Gateways</span>
                    </div>
                    <div className="enterprise-detail__summary-metric">
                        <span className={`enterprise-detail__summary-value ${sensorsOffline > 0 ? 'is-alert' : ''}`}>
                            {sensorsOffline}/{sensorsTotal}
                        </span>
                        <span className={`enterprise-detail__summary-sublabel ${sensorsOffline > 0 ? 'is-alert' : ''}`}>Sensors</span>
                    </div>
                </div>
            </div>

            <div className="enterprise-detail__summary-divider" />

            <div className="enterprise-detail__summary-section">
                <div className="enterprise-detail__summary-section-label">Alerts Disabled</div>
                <div className="enterprise-detail__summary-metrics">
                    <div className="enterprise-detail__summary-metric">
                        <span className={`enterprise-detail__summary-value ${locationsTotal > 0 && alertsDisabled === locationsTotal ? 'is-alert' : ''}`}>
                            {alertsDisabled}/{locationsTotal}
                        </span>
                        <span className={`enterprise-detail__summary-sublabel ${locationsTotal > 0 && alertsDisabled === locationsTotal ? 'is-alert' : ''}`}>Locations</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Location table (ReactTable — matches project conventions)
// ---------------------------------------------------------------------------

const TABS = [
    { key: 'attention', label: 'Needs Attention',     Icon: AttentionTabIcon },
    { key: 'offline',   label: 'Offline Locations',   Icon: OfflineTabIcon   },
    { key: 'other',     label: 'All Other Locations', Icon: OtherTabIcon     },
];

const LocationTable = ({ locations }) => {
    const [locationTab, setLocationTab] = useState('attention');

    // Strip the Unassigned entry (deviceGroupingId === null) — it exists only to
    // keep the sensor total in the summary bar correct and must not appear as a table row.
    const tableLocations = useMemo(
        () => locations.filter(loc => loc.deviceGroupingId !== null),
        [locations]
    );

    const tabCounts = useMemo(() => ({
        attention: filterByTab(tableLocations, 'attention').length,
        offline:   filterByTab(tableLocations, 'offline').length,
        other:     filterByTab(tableLocations, 'other').length,
    }), [tableLocations]);

    const filtered = useMemo(
        () => filterByTab(tableLocations, locationTab),
        [tableLocations, locationTab]
    );

    const columns = useMemo(() => {
        const cols = [
            {
                Header: 'Location',
                accessor: 'locationName',
                sortable: true,
                filterable: true,
                minResizeWidth: 140,
                width: 200,
                filterMethod: (filter, row) =>
                    String(row.locationName)
                        .toLowerCase()
                        .includes(filter.value.trim().toLowerCase()),
                Filter: ({ filter, onChange }) => (
                    <div className="dashboard-table-search">
                        <i className="table-search-icon" />
                        <input
                            type="text"
                            className="table-search-box"
                            value={filter ? filter.value : ''}
                            onChange={e => onChange(e.target.value)}
                            style={{ width: '100%' }}
                        />
                    </div>
                ),
                Cell: ({ value }) => <span>{value}</span>,
            },
            {
                Header: 'Critical Events',
                accessor: 'criticalAlertCount',
                sortable: true,
                minResizeWidth: 100,
                Cell: ({ value }) => (
                    <span className={value > 0 ? 'is-alert' : ''}>{value}</span>
                ),
            },
        ];

        FIXED_DEVICE_COLS.forEach(col => {
            cols.push({
                Header: col.label,
                id: col.key,
                accessor: row => getCellOffline(row, col),
                sortable: true,
                minResizeWidth: 100,
                sortMethod: nullSafeSort,
                Cell: ({ value }) => (
                    <span>{value === null ? '--' : value}</span>
                ),
            });
        });

        cols.push({
            Header: 'Alerts',
            id: 'alertsDisabled',
            accessor: row => !row.actionsEnabled,
            sortable: true,
            minResizeWidth: 100,
            Cell: ({ value }) => (
                <span className={value ? 'is-alert' : ''}>
                    {value ? 'Disabled' : 'Enabled'}
                </span>
            ),
        });

        return cols;
    }, []);

    return (
        <div className="enterprise-detail__locations">
            <div className="enterprise-detail__locations-header">
                <BuildingIcon />
                <h3 className="enterprise-detail__section-title">All Locations</h3>
            </div>

            <div className="enterprise-detail__location-tabs">
                {TABS.map(({ key, label, Icon }) => (
                    <button
                        key={key}
                        className={`enterprise-detail__location-tab ${locationTab === key ? 'is-active' : ''}`}
                        onClick={() => setLocationTab(key)}
                    >
                        <Icon />
                        <span>{label}</span>
                        <span className="enterprise-detail__tab-count">({tabCounts[key]})</span>
                    </button>
                ))}
            </div>

            <div className="enterprise-detail__tabs-select-wrap">
                <select
                    className="enterprise-detail__tabs-select"
                    value={locationTab}
                    onChange={e => setLocationTab(e.target.value)}
                >
                    {TABS.map(({ key, label }) => (
                        <option key={key} value={key}>
                            {label} ({tabCounts[key]})
                        </option>
                    ))}
                </select>
            </div>

            <ScrollModule />
            <ScrollArea
                speed={0.8}
                className="scroll-container"
                contentClassName="scrollable-content"
                horizontal
            >
                <div className="react-table-custom">
                    <ReactTable
                        className="-striped -highlight"
                        data={filtered}
                        columns={columns}
                        defaultSorted={[{ id: 'criticalAlertCount', desc: true }]}
                        showPaginationTop
                        defaultPageSize={10}
                    />
                </div>
            </ScrollArea>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Timeframe select styles (matches Status tab pattern)
// ---------------------------------------------------------------------------

const timeframeSelectStyles = {
    indicatorSeparator: base => ({ ...base, display: 'block' }),
    control: base => ({ ...base, minWidth: 160, cursor: 'pointer' }),
};

// ---------------------------------------------------------------------------
// Enterprise Detail — main export
// ---------------------------------------------------------------------------

const EnterpriseDetail = ({ enterprises, enterpriseId, onEnterpriseSelect, initialTimeframe }) => {
    const [currentId, setCurrentId] = useState(enterpriseId);
    const [timeframe, setTimeframe] = useState(initialTimeframe != null ? initialTimeframe : 24);
    const [locations, setLocations] = useState(null);
    const [loading, setLoading]     = useState(false);

    // Sync when navigated here from outside (card click or header search)
    useEffect(() => {
        setCurrentId(enterpriseId);
        if (initialTimeframe != null) {
            setTimeframe(initialTimeframe);
        }
    }, [enterpriseId, initialTimeframe]);

    // Fetch location detail when enterprise or timeframe changes
    const loadLocationDetail = useCallback((entId, hours) => {
        if (!entId) {
            setLocations(null);
            return;
        }
        setLoading(true);
        EnterprisesService.getLocationDetail(entId, hours).then(res => {
            setLocations(res.locations);
            setLoading(false);
        }).catch(err => {
            console.error('Failed to load location detail:', err);
            setLocations([]);
            setLoading(false);
        });
    }, []);

    useEffect(() => {
        loadLocationDetail(currentId, timeframe);
    }, [currentId, timeframe, loadLocationDetail]);

    const enterpriseOptions = (enterprises || []).map(e => ({ value: e.id, label: e.name }));
    const selectedOption    = enterpriseOptions.find(o => o.value === currentId) || null;

    const handleSelect = (opt) => {
        const id = opt ? opt.value : null;
        setCurrentId(id);
        if (onEnterpriseSelect) onEnterpriseSelect(id);
    };

    const summary = locations && locations.length > 0 ? buildSummary(locations) : null;

    return (
        <div className="enterprise-detail">
            <div className="enterprise-detail__selector-row">
                <Select
                    classNamePrefix="react-select"
                    className="enterprise-detail__selector"
                    options={enterpriseOptions}
                    value={selectedOption}
                    onChange={handleSelect}
                    placeholder="Select an enterprise…"
                    isClearable
                />
                {currentId && (
                    <div className="enterprise-detail__timeframe">
                        <span className="enterprise-detail__timeframe-label">Timeframe</span>
                        <Select
                            classNamePrefix="react-select"
                            options={TIMEFRAME_OPTIONS}
                            value={TIMEFRAME_OPTIONS.find(o => o.value === timeframe)}
                            onChange={o => setTimeframe(o.value)}
                            styles={timeframeSelectStyles}
                            isSearchable={false}
                        />
                    </div>
                )}
            </div>

            {!currentId ? (
                <div className="enterprise-detail__empty">
                    <p>Select an enterprise above to view location detail.</p>
                </div>
            ) : loading ? (
                <div className="enterprise-detail__loading">Loading...</div>
            ) : locations && locations.length > 0 ? (
                <>
                    <SummaryBar summary={summary} />
                    <LocationTable locations={locations} />
                </>
            ) : (
                <div className="enterprise-detail__empty">
                    <p>No location data available for this enterprise.</p>
                </div>
            )}
        </div>
    );
};

export default EnterpriseDetail;
